import { FC, PropsWithChildren } from "react";
import { NavigationContext } from "@excent-one/react/navigation/NavigationContext";
import type { NavigationItem } from "@excent-one/react/navigation/types";
import { useAuthorizedNavigationItems } from "@excent-one/auth";
import {
  homeIcon,
  groupIcon,
  folderIcon,
  fileIcon,
  listUnorderedSquareIcon,
  gridLayoutIcon,
} from "@progress/kendo-svg-icons";

// Paths are absolute (leading "/"), matching the router's basepath-stripped pathname -- the
// same shape `bff/me`'s route patterns are written against. Required for `isPathAuthorized`
// (used below via `useAuthorizedNavigationItems`, and by `ProtectedRoute`) to match correctly.
const navItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: homeIcon,
    hasParent: false,
    order: 1,
  },
  {
    id: "member-management",
    label: "Member Management",
    icon: groupIcon,
    hasParent: false,
    order: 2,
    children: [
      {
        id: "member-records",
        label: "Member Records",
        path: "/member-management/member-records",
        hasParent: true,
        order: 1,
      },
      {
        id: "member-change-request",
        label: "Member Record Change Request",
        path: "/member-management/change-request",
        hasParent: true,
        order: 2,
      },
    ],
  },
  {
    id: "deployment-management",
    label: "Deployment Management",
    icon: gridLayoutIcon,
    hasParent: false,
    order: 3,
    children: [
      {
        id: "my-calendar",
        label: "My Calendar",
        path: "/deployment/my-calendar",
        hasParent: true,
        order: 1,
      },
      {
        id: "monthly-coverage-plan",
        label: "Monthly Coverage Plan",
        path: "/deployment/monthly-coverage-plan",
        hasParent: true,
        order: 2,
      },
      {
        id: "schedule-change",
        label: "Schedule and Site Change Request",
        path: "/deployment/schedule-change",
        hasParent: true,
        order: 3,
      },
      {
        id: "daily-activity-monitoring",
        label: "Daily Activity Monitoring",
        path: "/deployment/daily-activity-monitoring-logs",
        hasParent: true,
        order: 4,
      },
      {
        id: "concerns-issues",
        label: "Concerns and Issues",
        path: "/deployment/concerns-issues",
        hasParent: true,
        order: 5,
      },
    ],
  },
  {
    id: "document-control",
    label: "Document Control",
    path: "/document-transmittal",
    icon: fileIcon,
    hasParent: false,
    order: 4,
  },
  {
    id: "incident-management",
    label: "Incident Management",
    path: "/incident-management",
    icon: folderIcon,
    hasParent: false,
    order: 5,
  },
  {
    id: "training-content",
    label: "Training Content & Online Forms",
    path: "/training-content",
    icon: listUnorderedSquareIcon,
    hasParent: false,
    order: 6,
  },
];

/**
 * Re-provides `NavigationContext` with the raw `navItems` filtered down to what the current
 * user is authorized for (`isPathAuthorized`, sourced from `bff/me` via the Arbitr IAS auth
 * provider). Nested inside the raw provider below so `useAuthorizedNavigationItems` -- which
 * reads `NavigationContext` itself -- sees the unfiltered list.
 */
const AuthorizedNavigationItems: FC<PropsWithChildren> = ({ children }) => {
  const [items, { isLoaded }] = useAuthorizedNavigationItems();

  return (
    <NavigationContext.Provider value={[items, { isLoaded }]}>
      {children}
    </NavigationContext.Provider>
  );
};

const AppNavigationItemsProvider: FC<PropsWithChildren> = ({ children }) => (
  <NavigationContext.Provider value={[navItems, { isLoaded: true }]}>
    <AuthorizedNavigationItems>{children}</AuthorizedNavigationItems>
  </NavigationContext.Provider>
);

export default AppNavigationItemsProvider;
